import React, { useState } from "react";
import { useTranslation } from "../../i18n/TranslationProvider";

import Nav from "react-bootstrap/Nav";
import Tab from "react-bootstrap/Tab";
import styled from "styled-components";

const PillLink = styled(Nav.Link)`
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  &:hover {
    filter: brightness(0.97);
    transform: translateY(-1px);
  }
`;

const AllSymptomsPreview = ({ topSymptoms }) => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState(null);

  const tSymptom = (category, factor) => {
    const key = `symptom.${category}.${(factor || "").toLowerCase()
      .replace(/ /g, "_").replace(/\//g, "_").replace(/,/g, "")
      .replace(/:/g, "").replace(/'/g, "").replace(/[()]/g, "").replace(/-/g, "_")
      .substring(0, 60)}`;
    const translated = t(key);
    return translated !== key ? translated : factor;
  };

  const messages = {
    cognitive: `${t("addsymptoms.category.cognitive")}: `,
    sleep: `${t("addsymptoms.category.sleep")}: `,
    physical: `${t("addsymptoms.category.physical")}: `,
    emotional: `${t("addsymptoms.category.emotional")}: `,
    speech: `${t("addsymptoms.category.speech")}: `,
    vision: `${t("addsymptoms.category.vision")}: `,
  };

  const tabs = {
    cognitive: t("addsymptoms.category.cognitive"),
    sleep: t("addsymptoms.category.sleep"),
    physical: t("addsymptoms.category.physical"),
    emotional: t("addsymptoms.category.emotional"),
    speech: t("addsymptoms.category.speech"),
    vision: t("addsymptoms.category.vision"),
  };

  const colors = {
    cognitive: "#edebf3", sleep: "#ffdadb", physical: "#ffdcaf",
    emotional: "#fff3c4", speech: "#cef3c4", vision: "#c1fdf7",
    wellness: "#dab7ec", travel: "#ffdadb", "screen time": "#ffdcaf",
    environmental: "#fff3c4", Dietary: "#cef3c4", situational: "#c1fdf7",
    Alternative: "#dab7ec", Applied: "#ffdadb", Chiropractic: "#ffdcaf",
    Vision: "#fff3c4", "Mental Health": "#c1fdf7", Other: "#f4f3ee",
  };

  const uniqueSymptoms = [];
  const factors = new Set();
  for (const symptom of topSymptoms || []) {
    if (!symptom?.factor) continue; // guard against malformed rows (null/missing factor)
    if (!factors.has(symptom.factor)) {
      factors.add(symptom.factor);
      uniqueSymptoms.push(symptom);
    }
  }

  const uniqueSubcategories = new Set();
  uniqueSymptoms.forEach((symptom) => uniqueSubcategories.add(symptom.subcategory));
  const uniqueSubcategoryArray = Array.from(uniqueSubcategories);

  if (uniqueSubcategoryArray.length === 0) return null;

  const currentActiveKey = activeKey || uniqueSubcategoryArray[0];

  return (
    <div
      style={{
        marginTop: 0,
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Tab.Container
        id="all-symptoms-preview-tabs"
        activeKey={currentActiveKey}
        onSelect={(key) => setActiveKey(key)}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            marginTop: "0.5rem",
            width: "100%",
            minWidth: 0,
            fontWeight: "normal",
            fontSize: "15px",
          }}
        >
          <Nav
            className="flex-column"
            style={{ gap: "0.5rem", width: "100%", minWidth: 0 }}
          >
            {uniqueSubcategoryArray.map((subcategory, i) => {
              const isActive = currentActiveKey === subcategory;
              return (
                <React.Fragment key={i}>
                  <Nav.Item style={{ width: "100%", minWidth: 0 }}>
                    <PillLink
                      eventKey={subcategory}
                      style={{
                        background: colors[subcategory],
                        border: isActive ? "2px solid #106995" : "1px solid rgba(16,105,149,0.35)",
                        borderRadius: "999px",
                        padding: "0.28rem 0.75rem",
                        fontSize: "12px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: isActive ? "#ffffff" : "#1a1a1a",
                        textShadow: isActive ? "0 1px 2px rgba(0,0,0,0.35)" : "none",
                        boxShadow: isActive
                          ? "0 0 0 2px rgba(16,105,149,0.35), 0 2px 6px rgba(0,0,0,0.15)"
                          : "0 1px 3px rgba(0,0,0,0.10)",
                        textAlign: "center",
                        display: "block",
                        boxSizing: "border-box",
                      }}
                    >
                      {tabs[subcategory]}
                    </PillLink>
                  </Nav.Item>
                  {/* Expanded symptom box now renders immediately below its
                      own pill (only when that pill is active), instead of
                      always appearing after the full pill list via a
                      separate Tab.Content block. */}
                  {isActive && (
                    <Tab.Content style={{ width: "100%", minWidth: 0 }}>
                      <Tab.Pane eventKey={subcategory} active>
                        <div
                          style={{
                            background: colors[subcategory],
                            border: "1px solid rgba(16,105,149,0.35)",
                            borderRadius: "12px",
                            padding: "0.5rem 0.75rem",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                            boxSizing: "border-box",
                            width: "100%",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "baseline",
                              gap: "0.35rem",
                            }}
                          >
                            <h6 className="mb-0" style={{ letterSpacing: "0.2px", fontSize: "13px" }}>
                              {messages[subcategory]}
                            </h6>
                            {uniqueSymptoms
                              .filter((symptom) => symptom.subcategory === subcategory)
                              .map((filteredSymptom, j) => (
                                <span key={j} style={{ fontSize: "13px" }}>
                                  &bull; {tSymptom(subcategory, filteredSymptom.factor)}
                                </span>
                              ))}
                          </div>
                        </div>
                      </Tab.Pane>
                    </Tab.Content>
                  )}
                </React.Fragment>
              );
            })}
          </Nav>
        </div>
      </Tab.Container>
    </div>
  );
};

export default AllSymptomsPreview;